import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CircularGallery from '../components/CircularGallery';
import { useTranslation } from 'react-i18next';
import { useServices } from '../contexts/services/ServiceContext';
import { useAuth } from '../contexts/auth/AuthContext';
import SEO from '../components/SEO';

function Home() {
  const { t } = useTranslation(['home', 'common']);
  const navigate = useNavigate();
  const { services, categories } = useServices();
  const { isAuthenticated } = useAuth();

  const handleTopUpClick = () => {
    navigate(isAuthenticated ? '/profile' : '/login');
  };

  const [showPromoPopup, setShowPromoPopup] = useState(true);

  // Quick booking form state
  const [quickBooking, setQuickBooking] = useState({
    serviceId: '',
    date: ''
  });

  // Get cheapest service from each category for pricing guide
  const getCheapestServiceByCategory = () => {
    const cheapestServices: { [categoryId: string]: any } = {};

    categories.forEach(category => {
      if (category.services && category.services.length > 0) {
        // Find the service with the lowest price in this category
        const cheapest = category.services.reduce((min, service) => {
          const currentPrice = service.numericPrice || parseFloat(String(service.price)) || 0;
          const minPrice = min.numericPrice || parseFloat(String(min.price)) || 0;
          return currentPrice < minPrice ? service : min;
        });

        cheapestServices[category.id] = {
          ...cheapest,
          categoryName: category.name || category.title || 'Service'
        };
      }
    });

    return cheapestServices;
  };

  const cheapestServices = getCheapestServiceByCategory();

  // Find the LA MER Revitalizing Hydration Facial for promotion
  const promoService = services.find(s => s.name.includes('Revitalizing Hydration'));

  const handlePromoClick = () => {
    navigate('/promotion');
  };

  // Handle quick booking form submission
  const handleQuickBooking = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that a service is selected
    if (!quickBooking.serviceId) {
      alert('Please select a service');
      return;
    }

    // Find the selected service from the services list
    const selectedService = services.find(service => service.id === quickBooking.serviceId);

    if (selectedService) {
      // Store selected service in localStorage (same pattern as Services page)
      localStorage.setItem('selectedService', JSON.stringify({
        id: selectedService.id,
        name: selectedService.name,
        duration: selectedService.numericDuration || 0,
        price: selectedService.numericPrice || 0,
        category_id: selectedService.category_id
      }));

      // Store booking preferences if date is provided
      if (quickBooking.date) {
        localStorage.setItem('bookingPreferences', JSON.stringify({
          preferredDate: quickBooking.date
        }));
      }

      // Navigate to booking page
      navigate('/booking');
    }
  };

  // Toggle this flag to switch between video and image
  const useVideo = true; // set to true for video, false for image
  return (
    <>
      <SEO
        title="Healing Lotus Spa | Luxury Massage, Facials & Wellness in Dallas, TX"
        description="Dallas's premier medical-grade spa offering signature massages, anti-aging facials, body treatments, and wellness memberships. Book your appointment at Healing Lotus Skin Spa."
        path="/"
      />
      <section className="relative w-full h-screen flex items-center justify-center overflow-hidden bg-black">
        {/* Original Pexels image: https://images.pexels.com/photos/3757942/pexels-photo-3757942.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1 */}
        <img
          className="absolute inset-0 w-full h-full object-cover"
          src="/images/hero-image.jpg"
          alt="Healing Lotus Skin Spa in Dallas — tranquil spa ambiance with calming elements"
        />
        <div className="absolute inset-0 bg-[#232347]/30" />
        <div className="relative z-10 flex flex-col items-start justify-center h-full w-full max-w-5xl mx-auto px-6">
          <span className="text-lg md:text-xl text-white font-light mb-2">Healing Lotus Skin Spa · Dallas, TX</span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 drop-shadow-lg italic">Where Beauty Rises from Calm</h1>
          <p className="text-xl md:text-2xl text-white/90 mb-6 max-w-2xl leading-relaxed">
            We exist for those who seek transformation without spectacle, confidence without noise, and beauty that is grounded—not manufactured.
          </p>
          <Link to="/services" className="bg-[#B2D8C5] hover:bg-[#88bfa3] text-white px-8 py-3 rounded-md font-semibold text-lg transition-colors inline-flex items-center">
            {t('common:buttons.bookYourExperience')}
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </Link>
        </div>
      </section>

      {/* LA MER Promotion Banner */}
      {promoService && (
        <section className="py-12 md:py-16 px-4 bg-gradient-to-r from-[#F9F3E3] via-[#FDF8EF] to-[#F9F3E3] relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A96E] to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A96E] to-transparent"></div>
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#C9A96E]/5 blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-[#C9A96E]/5 blur-3xl"></div>

          <div className="container mx-auto max-w-5xl relative">
            <div
              onClick={handlePromoClick}
              className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 border border-[#C9A96E]/20"
            >
              <div className="flex flex-col md:flex-row">
                {/* Image */}
                <div className="w-full md:w-2/5 relative">
                  <img
                    src="/images/lamer.jpeg"
                    alt="LA MER The Revitalizing Hydration Facial"
                    className="w-full h-64 md:h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="inline-block px-4 py-2 bg-[#C41E3A] text-white text-sm font-bold rounded-full shadow-lg animate-pulse">
                      LIMITED TIME OFFER
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="w-full md:w-3/5 p-6 md:p-10 flex flex-col justify-center">
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 bg-[#C9A96E]/10 text-[#C9A96E] text-xs font-semibold tracking-widest uppercase rounded-full border border-[#C9A96E]/30">
                      Exclusive Promotion
                    </span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#232347] mb-2 leading-tight">
                    Buy 1 Get 1 <span className="text-[#C41E3A]">FREE</span>
                  </h2>
                  <h3 className="text-lg md:text-xl font-serif text-[#232347]/80 mb-4">
                    LA MER The Revitalizing Hydration Facial
                  </h3>
                  <p className="text-[#232347]/70 mb-6 leading-relaxed text-sm md:text-base">
                    An immersive rejuvenating ritual powered by La Mer's legendary Miracle Broth. This deeply restorative treatment infuses intense hydration, soothes stressed skin, and awakens a radiant complexion.
                  </p>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-2xl font-semibold text-[#232347]">
                      ${promoService.numericPrice || promoService.price}
                    </span>
                    <span className="text-[#232347]/50">|</span>
                    <span className="text-[#232347]/60">{promoService.numericDuration || promoService.duration} min</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePromoClick();
                    }}
                    className="self-start inline-flex items-center bg-[#C9A96E] hover:bg-[#B8954F] text-white px-8 py-3.5 rounded-lg font-semibold text-lg transition-all shadow-md hover:shadow-lg"
                  >
                    Book Now
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Quick Booking Section */}
      <section className="py-16 px-4 bg-[#B2D8C5]/10">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-serif font-semibold text-[#232347] mb-3 md:mb-6 text-center">{t('home:quickBooking.title')}</h2>
          <p className="text-[#232347]/80 mb-6 md:mb-8 text-center text-sm md:text-base">{t('home:quickBooking.description')}</p>
          
          <div className="bg-white rounded-xl shadow-lg p-5 md:p-8">
            <form onSubmit={handleQuickBooking} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="space-y-1 md:space-y-2">
                <label htmlFor="service" className="block text-sm font-medium text-[#232347]">Select Service</label>
                <select
                  id="service"
                  value={quickBooking.serviceId}
                  onChange={(e) => setQuickBooking({...quickBooking, serviceId: e.target.value})}
                  className="w-full rounded bg-[#F8F6F2] border border-[#B2D8C5] px-3 md:px-4 py-2 md:py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
                  required
                >
                  <option value="">Choose a service...</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name} - ${service.numericPrice || service.price}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1 md:space-y-2">
                <label htmlFor="date" className="block text-sm font-medium text-[#232347]">Preferred Date (Optional)</label>
                <input
                  type="date"
                  id="date"
                  value={quickBooking.date}
                  onChange={(e) => setQuickBooking({...quickBooking, date: e.target.value})}
                  className="w-full rounded bg-[#F8F6F2] border border-[#B2D8C5] px-3 md:px-4 py-2 md:py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
                />
              </div>
              
              <div className="md:col-span-2 mt-2">
                <button type="submit" className="w-full bg-[#B2D8C5] hover:bg-[#88bfa3] text-white py-3 rounded-md font-semibold text-base md:text-lg transition-colors flex items-center justify-center">
                  Check Availability
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="w-full py-16 px-4 bg-white/30">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 text-center flex flex-col items-center shadow-md hover:shadow-lg transition-shadow">
              <div className="bg-[#F7CAC9]/20 p-4 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#F7CAC9]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#232347] mb-2 font-serif">Expert Therapists</h3>
              <p className="text-[#232347]/70 text-sm">Our certified therapists bring years of experience to every treatment.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 text-center flex flex-col items-center shadow-md hover:shadow-lg transition-shadow">
              <div className="bg-[#F7CAC9]/20 p-4 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#F7CAC9]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#232347] mb-2 font-serif">Customized Treatments</h3>
              <p className="text-[#232347]/70 text-sm">Personalized services tailored to your unique needs and preferences.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 text-center flex flex-col items-center shadow-md hover:shadow-lg transition-shadow">
              <div className="bg-[#F7CAC9]/20 p-4 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#F7CAC9]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#232347] mb-2 font-serif">Premium Products</h3>
              <p className="text-[#232347]/70 text-sm">Organic, sustainable, and high-quality products for all treatments.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 text-center flex flex-col items-center shadow-md hover:shadow-lg transition-shadow">
              <div className="bg-[#F7CAC9]/20 p-4 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#F7CAC9]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#232347] mb-2 font-serif">Luxurious Experience</h3>
              <p className="text-[#232347]/70 text-sm">Every detail designed to create a tranquil and rejuvenating atmosphere.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Services (updated above) */}
      <section className="w-full py-16 px-4 bg-white/50">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-6 text-center">Our Services</h2>
          <p className="text-[#232347]/80 mb-12 text-center max-w-3xl mx-auto">Discover our range of premium spa treatments designed to relax, rejuvenate, and restore balance to your body and mind.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Massage Therapy */}
            <div className="group relative rounded-xl overflow-hidden shadow-lg">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/3865676/pexels-photo-3865676.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Relaxing massage therapy session at Healing Lotus Spa Dallas with aromatic oils"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#232347]/90 via-transparent to-transparent flex flex-col justify-end p-6">
                <h3 className="text-xl font-semibold text-white mb-2 font-serif">Massage Therapy</h3>
                <p className="text-white/90 text-sm mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">Relieve tension and promote deep relaxation with our therapeutic massage services.</p>
                <Link to="/services" className="text-[#B2D8C5] text-sm font-medium group-hover:underline transition-all flex items-center">
                  Explore Services
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </div>
            
            {/* Facial Treatments */}
            <div className="group relative rounded-xl overflow-hidden shadow-lg">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/3997993/pexels-photo-3997993.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Professional facial treatment at a Dallas luxury spa with natural skincare products"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#232347]/90 via-transparent to-transparent flex flex-col justify-end p-6">
                <h3 className="text-xl font-semibold text-white mb-2 font-serif">Facial Treatments</h3>
                <p className="text-white/90 text-sm mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">Revitalize your skin with our customized facials for a healthy, radiant complexion.</p>
                <Link to="/services" className="text-[#B2D8C5] text-sm font-medium group-hover:underline transition-all flex items-center">
                  Explore Services
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </div>
            
            {/* Body Treatments */}
            <div className="group relative rounded-xl overflow-hidden shadow-lg">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/3188/love-romantic-bath-candlelight.jpg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Luxurious body treatment at Healing Lotus Dallas with candles and aromatic bath"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#232347]/90 via-transparent to-transparent flex flex-col justify-end p-6">
                <h3 className="text-xl font-semibold text-white mb-2 font-serif">Body Treatments</h3>
                <p className="text-white/90 text-sm mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">Indulge in our premium body treatments designed for total relaxation and rejuvenation.</p>
                <Link to="/services" className="text-[#B2D8C5] text-sm font-medium group-hover:underline transition-all flex items-center">
                  Explore Services
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </div>
            
            {/* Specialty Treatments */}
            <div className="group relative rounded-xl overflow-hidden shadow-lg">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/4041392/pexels-photo-4041392.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Specialty hot stone spa treatment at Healing Lotus Spa Dallas with premium oils"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#232347]/90 via-transparent to-transparent flex flex-col justify-end p-6">
                <h3 className="text-xl font-semibold text-white mb-2 font-serif">Specialty Treatments</h3>
                <p className="text-white/90 text-sm mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">Experience our signature treatments that combine multiple techniques for the ultimate spa experience.</p>
                <Link to="/services" className="text-[#B2D8C5] text-sm font-medium group-hover:underline transition-all flex items-center">
                  Explore Services
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Price Guide */}
      <section className="py-16 px-4 bg-white/30">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-6 text-center">Pricing Guide</h2>
          <p className="text-[#232347]/80 mb-12 text-center max-w-3xl mx-auto">Our most popular services at a glance. Visit our services page for the complete menu.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Dynamic Pricing Cards - Show cheapest service from each category */}
            {categories.slice(0, 4).map((category) => {
              const cheapest = cheapestServices[category.id];
              if (!cheapest) return null;

              return (
                <div key={category.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-serif font-semibold text-[#232347] mb-4">
                    {category.name || category.title}
                  </h3>
                  <div className="mb-4">
                    <p className="text-sm text-[#232347]/70 mb-1">{cheapest.name}</p>
                    <div className="flex items-baseline gap-2">
                      <p className="text-2xl font-semibold text-[#232347]">
                        ${cheapest.numericPrice || cheapest.price}
                      </p>
                      <p className="text-sm text-[#232347]/60">
                        {cheapest.numericDuration || cheapest.duration} min
                      </p>
                    </div>
                  </div>
                  <Link to="/services" className="text-[#B2D8C5] font-medium hover:underline flex items-center">
                    View Details
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </Link>
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-10">
            <Link to="/services" className="inline-flex items-center justify-center bg-[#232347]/10 hover:bg-[#232347]/20 text-[#232347] px-6 py-3 rounded-md font-medium transition-colors">
              View Complete Service Menu
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Wallet Top-Up Bonus Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#B2D8C5]/20 via-white to-[#F7CAC9]/20 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#B2D8C5]/10 blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#F7CAC9]/10 blur-3xl"></div>

        <div className="container mx-auto max-w-5xl relative">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-4 text-center">Wallet Top-Up Bonus</h2>
          <p className="text-[#232347]/80 mb-12 text-center max-w-2xl mx-auto">
            Add funds to your wallet and receive bonus credit. The more you top up, the bigger the bonus!
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { topUp: 500, bonus: 100, total: 600 },
              { topUp: 1000, bonus: 300, total: 1300 },
              { topUp: 2000, bonus: 1000, total: 3000 },
              { topUp: 3000, bonus: 1500, total: 4500 },
              { topUp: 4000, bonus: 2500, total: 6500 },
              { topUp: 5000, bonus: 3000, total: 8000 },
            ].map((tier) => (
              <div key={tier.topUp} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow text-center">
                <p className="text-2xl font-semibold text-[#232347] mb-2">${tier.topUp.toLocaleString()}</p>
                <span className="inline-block px-3 py-1 bg-[#B2D8C5]/20 text-[#B2D8C5] text-sm font-medium rounded-full mb-3">
                  +${tier.bonus.toLocaleString()} Bonus
                </span>
                <p className="text-[#232347]/70 text-sm">Total credit: <span className="font-semibold text-[#232347]">${tier.total.toLocaleString()}</span></p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={handleTopUpClick}
              className="inline-flex items-center justify-center bg-[#B2D8C5] hover:bg-[#88bfa3] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-md hover:shadow-lg transform hover:-translate-y-1"
            >
              Top Up Now
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2 rounded-xl overflow-hidden shadow-xl">
              <img
                src="https://images.pexels.com/photos/3865675/pexels-photo-3865675.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                srcSet="
                  https://images.pexels.com/photos/3865675/pexels-photo-3865675.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1 600w,
                  https://images.pexels.com/photos/3865675/pexels-photo-3865675.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1 1260w
                "
                sizes="(max-width: 1023px) 100vw, 50vw"
                alt="Tranquil spa treatment room at Healing Lotus Dallas with hot stones and candles"
                className="w-full h-[400px] object-cover"
                loading="lazy"
              />
            </div>
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-6">About Healing Lotus Skin Spa</h2>
              <h3 className="text-xl font-serif italic text-[#232347] mb-4">Emotional Skincare & Healing Aesthetics · Awakening the Skin Through Sound</h3>
              <p className="text-[#232347]/80 mb-4 leading-relaxed">
                True beauty begins when emotions are gently held.
              </p>
              <p className="text-[#232347]/80 mb-6 leading-relaxed">
                Emotional Skincare Healing Aesthetics integrates skin science, nervous system relaxation, and sound therapy. We believe skin responds not only to ingredients, but to emotions. When stress, anxiety, and inner tension are released, skin naturally returns to balance and radiance.
              </p>
              <p className="text-[#232347]/80 mb-6 leading-relaxed">
                Each treatment pairs professional skincare with therapeutic sound frequencies—low-frequency singing bowls, ambient tones, rhythmic breathing guidance, and healing sound waves. These sounds calm the nervous system and activate the body's natural repair mode.
              </p>
              <div className="bg-[#F1F7F4] rounded-lg p-4 mb-6">
                <p className="text-sm text-[#232347]/90 mb-2 font-medium">As sound moves through the space:</p>
                <ul className="text-sm text-[#232347]/80 space-y-1">
                  <li>• The mind quiets</li>
                  <li>• Breathing deepens</li>
                  <li>• Tension softens</li>
                  <li>• Skin absorption enhances</li>
                </ul>
              </div>
              <p className="text-[#232347]/80 mb-6 leading-relaxed italic">
                Skincare transcends the surface, reaching emotional, neurological, and energetic layers—allowing the skin to heal while being truly "heard."
              </p>
              <p className="text-[#232347] font-medium mb-8">
                This is not simply a beauty treatment—<br />
                <span className="text-[#232347] font-semibold">it's a journey of emotional restoration and skin rebirth.</span>
              </p>
              <Link to="/about" className="inline-flex items-center justify-center bg-[#F7CAC9] hover:bg-[#e6b9b8] text-white px-6 py-3 rounded-md font-medium transition-colors">
                Learn More About Us
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Updated */}
      <section className="py-20 px-4 bg-[#232347]/5">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-6 text-center">Client Testimonials</h2>
          <p className="text-[#232347]/80 mb-12 text-center max-w-3xl mx-auto">Hear what our clients have to say about their experiences at Healing Lotus Skin Spa.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-xl shadow-md p-8 relative">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-[#B2D8C5] rounded-full w-12 h-12 flex items-center justify-center border-4 border-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <div className="flex justify-center mb-4 pt-2">
                <div className="flex gap-1 text-[#B2D8C5] text-lg" aria-label="5 out of 5 stars rating">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} role="img" aria-hidden="true">★</span>
                  ))}
                </div>
              </div>
              <p className="text-[#232347]/80 text-center mb-6 italic">
                "The atmosphere at Healing Lotus is truly transformative. From the moment I stepped in, I felt the stress melt away. The massage was incredible, and the staff made me feel so special."
              </p>
              <div className="text-center">
                <p className="font-semibold text-[#232347]">Sarah T.</p>
                <p className="text-[#232347]/60 text-sm">Dallas | Regular Client</p>
              </div>
            </div>
            
            {/* Testimonial 2 */}
            <div className="bg-white rounded-xl shadow-md p-8 relative">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-[#B2D8C5] rounded-full w-12 h-12 flex items-center justify-center border-4 border-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <div className="flex justify-center mb-4 pt-2">
                <div className="flex gap-1 text-[#B2D8C5] text-lg" aria-label="5 out of 5 stars rating">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} role="img" aria-hidden="true">★</span>
                  ))}
                </div>
              </div>
              <p className="text-[#232347]/80 text-center mb-6 italic">
                "Great experience from start to finish. The aromatherapy and attention to detail made my facial treatment exceptional. The staff at Healing Lotus are true professionals who know exactly what your skin needs."
              </p>
              <div className="text-center">
                <p className="font-semibold text-[#232347]">Amanda R.</p>
                <p className="text-[#232347]/60 text-sm">Dallas | Monthly Member</p>
              </div>
            </div>
            
            {/* Testimonial 3 */}
            <div className="bg-white rounded-xl shadow-md p-8 relative">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-[#B2D8C5] rounded-full w-12 h-12 flex items-center justify-center border-4 border-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <div className="flex justify-center mb-4 pt-2">
                <div className="flex gap-1 text-[#B2D8C5] text-lg" aria-label="5 out of 5 stars rating">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} role="img" aria-hidden="true">★</span>
                  ))}
                </div>
              </div>
              <p className="text-[#232347]/80 text-center mb-6 italic">
                "My first visit to Healing Lotus Spa exceeded all my expectations - the place is gorgeous, clean and peaceful. The treatments were incredible and I can't wait to return!"
              </p>
              <div className="text-center">
                <p className="font-semibold text-[#232347]">Michael D.</p>
                <p className="text-[#232347]/60 text-sm">Dallas | First-time Guest</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#F7CAC9]/30 via-white to-[#B2D8C5]/30 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#F7CAC9]/10 blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#B2D8C5]/10 blur-3xl"></div>
        
        <div className="container mx-auto max-w-5xl relative">
          <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-xl p-8 md:p-12 border border-white/50">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              {/* Left: Text Content */}
              <div className="w-full md:w-2/3">
                <span className="inline-block px-4 py-1 bg-[#B2D8C5]/20 text-[#232347] text-sm font-medium rounded-full mb-4">Begin Your Journey</span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-[#232347] mb-6 leading-tight">Transform Your Well-being Today</h2>
                <p className="text-[#232347]/80 text-lg mb-8">
                  Experience the perfect blend of ancient wisdom and modern techniques in our tranquil sanctuary. Our expert therapists are ready to guide you on your wellness journey.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/services" className="inline-flex items-center justify-center bg-[#B2D8C5] hover:bg-[#88bfa3] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-md hover:shadow-lg transform hover:-translate-y-1">
                    Book Your Session
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </Link>
                  <Link to="/membership" className="inline-flex items-center justify-center bg-white border-2 border-[#F7CAC9] text-[#232347] hover:bg-[#F7CAC9]/10 px-8 py-3.5 rounded-lg font-medium text-lg transition-all">
                    Join Membership
                  </Link>
                </div>
              </div>
              
            </div>
          </div>
        </div>
      </section>

      {/* Floating Promo Popup - Bottom Left */}
      {promoService && showPromoPopup && (
        <div className="fixed bottom-6 left-6 z-50 animate-[slideUp_0.5s_ease-out]">
          <div
            onClick={handlePromoClick}
            className="relative bg-white rounded-xl shadow-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.2)] hover:-translate-y-1 border border-[#C9A96E]/30 w-[320px]"
          >
            {/* Close button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowPromoPopup(false);
              }}
              className="absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white text-xs transition-colors"
              aria-label="Close promotion"
            >
              ✕
            </button>

            {/* Image */}
            <div className="relative h-36">
              <img
                src="/images/lamer.jpeg"
                alt="LA MER Promotion"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-3 left-3">
                <span className="inline-block px-3 py-1 bg-[#C41E3A] text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                  BUY 1 GET 1 FREE
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <h4 className="font-serif font-semibold text-[#232347] text-sm leading-snug mb-1">
                LA MER Revitalizing Hydration Facial
              </h4>
              <p className="text-[#232347]/60 text-xs mb-3">
                Powered by La Mer's legendary Miracle Broth
              </p>
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-[#232347]">
                  ${promoService.numericPrice || promoService.price}
                </span>
                <span className="inline-flex items-center bg-[#C9A96E] text-white px-4 py-1.5 rounded-lg text-sm font-semibold">
                  Book Now
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Home; 